import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { Button } from '../ui/button'
import { useTranslation } from 'react-i18next'

export interface PaginationData {
  page: number
  page_size: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

interface PaginationProps {
  pagination: PaginationData
  onPageChange: (page: number) => void
  className?: string
}

export function Pagination({ pagination, onPageChange, className = '' }: PaginationProps) {
  const { t } = useTranslation('common')
  const { page, total_pages, has_next, has_prev, total_count, page_size } = pagination

  if (total_pages <= 1) {
    return null
  }

  // Calculate the range of items being displayed
  const startItem = ((page - 1) * page_size) + 1
  const endItem = Math.min(page * page_size, total_count)

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 7 // Maximum number of page buttons to show
    
    if (total_pages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)
      
      if (page > 3) {
        pages.push('...')
      }
      
      // Show pages around current page
      const start = Math.max(2, page - 1)
      const end = Math.min(total_pages - 1, page + 1)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      if (page < total_pages - 2) {
        pages.push('...')
      }
      
      // Always show last page
      pages.push(total_pages)
    }
    
    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <div className={`pagination-container flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {/* Results info */}
      <div className="text-sm text-muted-foreground">
        {t('pagination.showing', { start: startItem, end: endItem, total: total_count })}
      </div>

      {/* Pagination controls */}
      <div className="pagination-controls flex items-center gap-2">
        {/* First page button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(1)}
          disabled={!has_prev}
          className="h-9 w-9"
          aria-label={t('pagination.firstPage')}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>

        {/* Previous page button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(page - 1)}
          disabled={!has_prev}
          className="h-9 w-9"
          aria-label={t('pagination.previousPage')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Page number buttons */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((pageNum, index) => {
            if (pageNum === '...') {
              return (
                <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                  ...
                </span>
              )
            }
            
            const isCurrentPage = pageNum === page
            
            return (
              <Button
                key={pageNum}
                variant={isCurrentPage ? 'default' : 'outline'}
                size="sm"
                onClick={() => onPageChange(pageNum as number)}
                className="h-9 min-w-[36px]"
                aria-label={t('pagination.page', { page: pageNum })}
                aria-current={isCurrentPage ? 'page' : undefined}
              >
                {pageNum}
              </Button>
            )
          })}
        </div>

        {/* Next page button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(page + 1)}
          disabled={!has_next}
          className="h-9 w-9"
          aria-label={t('pagination.nextPage')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Last page button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(total_pages)}
          disabled={!has_next}
          className="h-9 w-9"
          aria-label={t('pagination.lastPage')}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}



